/**
 * Copyright 2022 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { requestBaseURL } from '@/config/serviceAPI';
import { getAxios } from '@/lib/axios';
import { type AxiosInstance } from 'axios';
import { useMutation } from '@tanstack/react-query';
import type {
  AddToCartData,
  AddToCartResponse,
  AddToCartPayload,
} from '@/types/reduxStore/cartUpdate';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';
import { useAcesDealsVehicleAttributes } from '@/features/deals/hooks/useAcesDealsVehicleAttributes';
import { usePreferredVehicle } from '@/features/header/hooks/usePreferredVehicle';

const URL = `${requestBaseURL}/ecomm/b2c/order/v4/item`;
const ACES_URL = `${requestBaseURL}/ecomm/b2c/order/v5/item`;

const addToCart = async (options: AddToCartPayload, axiosInstance?: AxiosInstance) => {
  const {
    skuId,
    productId,
    quantity,
    fulfillmentTypeId,
    vehicleId,
    storeNumber,
    searchedTerm,
    configData,
    xFusionQueryId,
    vehicleAttributes,
    acesEnabled,
  } = options;

  const response = await getAxios(axiosInstance).post<AddToCartResponse>(
    acesEnabled ? ACES_URL : URL,
    {
      skuId,
      productId,
      quantity,
      fulfillmentTypeId,
      vehicleId,
      storeNumber,
      configData,
      searchedTerm,
      xFusionQueryId,
      ...(acesEnabled && vehicleAttributes && { vehicleAttributes }),
    }
  );

  return selector(response.data);
};

const selector = (data: AddToCartResponse): AddToCartData => data;

export const useAddToCart = ({ onAddToCartSuccess }: { onAddToCartSuccess?: () => void } = {}) => {
  const preferredVehicle = usePreferredVehicle();
  const acesVehicleAttributes = useAcesDealsVehicleAttributes({
    engine: preferredVehicle?.engine,
    makeName: preferredVehicle?.make,
    modelName: preferredVehicle?.model,
    regionName: preferredVehicle?.region,
    subModelName: preferredVehicle?.subModel,
  });
  const { acesEnabled } = useAcesFlags();
  return useMutation({
    mutationFn: ({
      skuId,
      productId,
      quantity,
      fulfillmentTypeId,
      vehicleId,
      storeNumber,
      configData,
      searchedTerm,
      xFusionQueryId,
    }: Omit<AddToCartPayload, 'vehicleAttributes' | 'acesEnabled'>) =>
      addToCart({
        skuId,
        productId,
        quantity,
        fulfillmentTypeId,
        vehicleId,
        storeNumber,
        configData,
        searchedTerm,
        xFusionQueryId,
        acesEnabled,
        ...(acesVehicleAttributes && {
          vehicleAttributes: acesVehicleAttributes,
        }),
      }),
    onSuccess: () => {
      onAddToCartSuccess?.();
    },
  });
};
